import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import productService from '../services/productService';
import measureService from '../services/measureService';
import ProductTable from '../components/ProductTable';
import FilterPanel from '../components/FilterPanel';
import { useNotification } from '../context/NotificationContext';

const ProductsPage = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { notify } = useNotification();
  const canModify = user?.is_staff || false;

  const [products, setProducts] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ name: '', brand: '', measure: '' });
  const [sort, setSort] = useState({ ordering: '-created_at' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [productsRes, measuresRes] = await Promise.all([
        productService.listProducts({}),
        measureService.listMeasures()
      ]);

      setProducts(productsRes.data.results || []);
      setMeasures(measuresRes.data.results || []);

      const uniqueBrands = [...new Set(productsRes.data.results.map(p => p.brand))].sort();
      setBrands(uniqueBrands);
    } catch (err) {
      notify.error(t('products.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = async (newFilters) => {
    setFilters(newFilters);
    try {
      const response = await productService.listProducts({ ...newFilters, ...sort });
      setProducts(response.data.results || []);
    } catch (err) {
      notify.error(t('products.failedToFilter'));
    }
  };

  const handleSort = async (newSort) => {
    setSort(newSort);
    try {
      const response = await productService.listProducts({ ...filters, ...newSort });
      setProducts(response.data.results || []);
    } catch (err) {
      notify.error(t('products.failedToSort'));
    }
  };

  const handleEdit = (id) => {
    navigate(`/products/${id}/edit`);
  };

  const handleToggleDelete = async (id) => {
    try {
      await productService.toggleDelete(id);
      fetchData();
    } catch (err) {
      notify.error(t('products.failedToToggleDelete'));
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0, color: '#3D3D3D' }}>Products</h1>
        {canModify && (
          <button
            onClick={() => navigate('/products/create')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#A0BD46',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            + Add Product
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#3D3D3D' }}>
          Loading products...
        </div>
      ) : (
        <>
          <FilterPanel
            brands={brands}
            measures={measures}
            onFilter={handleFilter}
            onSort={handleSort}
          />

          {products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#3D3D3D' }}>
              No products found.
            </div>
          ) : (
            <ProductTable
              products={products}
              onEdit={handleEdit}
              onToggleDelete={handleToggleDelete}
              canModify={canModify}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProductsPage;
